import { useCallback, useEffect, useState } from 'react';
import { listRescue, createRescue, updateRescue, deleteRescue } from '../../lib/api/adminApi';

const TYPES = [
  { value: 'private_server', label: 'Serveur communautaire' },
  { value: 'community_patch', label: 'Patch communautaire' },
  { value: 'offline_fix', label: 'Correctif hors-ligne' },
  { value: 'archive', label: 'Archive / abandonware' },
];

// Check-list de sécurité. Les critères "critical" doivent être cochés pour pouvoir marquer "Vérifié".
const CHECKLIST = [
  { key: 'free', critical: true, label: 'Gratuit — aucun paiement obligatoire pour jouer' },
  { key: 'official', critical: true, label: 'Lien vers le site / GitHub / Discord OFFICIEL du projet (pas un téléchargement direct)' },
  { key: 'noav', critical: true, label: "Ne demande PAS de désactiver l'antivirus" },
  { key: 'established', critical: true, label: 'Projet établi : open-source, communauté active, ou couvert par la presse' },
  { key: 'checked', critical: false, label: 'Recherché « <projet> scam / reddit » + scan VirusTotal si téléchargement' },
];

const EMPTY = { id: 0, steamAppId: '', type: 'private_server', title: '', url: '', description: '', source: '', verified: false };
const noChecks = () => CHECKLIST.reduce((a, c) => ({ ...a, [c.key]: false }), {});
const allChecks = () => CHECKLIST.reduce((a, c) => ({ ...a, [c.key]: true }), {});

function AdminRescue({ onAuthError }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // objet en édition, ou null
  const [checks, setChecks] = useState(noChecks());
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    listRescue()
      .then(({ data }) => setList(data || []))
      .catch((err) => onAuthError && onAuthError(err))
      .finally(() => setLoading(false));
  }, [onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const criticalOk = CHECKLIST.filter((c) => c.critical).every((c) => checks[c.key]);

  const openNew = () => {
    setForm({ ...EMPTY });
    setChecks(noChecks());
    setError(null);
  };
  const openEdit = (l) => {
    setForm({ ...EMPTY, ...l });
    setChecks(l.verified ? allChecks() : noChecks()); // un lien déjà vérifié : cases pré-cochées
    setError(null);
  };

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const showToast = (m) => {
    setToast(m);
    setTimeout(() => setToast(null), 3000);
  };

  const submit = async (e) => {
    e.preventDefault();
    // On ne peut passer "vérifié" que si les critères critiques sont cochés.
    const verified = form.verified && criticalOk;
    const payload = {
      steamAppId: Number(form.steamAppId),
      type: form.type,
      title: form.title,
      url: form.url,
      description: form.description || null,
      source: form.source || null,
      verified,
    };
    try {
      if (form.id) await updateRescue(form.id, payload);
      else await createRescue(payload);
      setForm(null);
      await load();
      showToast(verified ? 'Lien publié ✓' : 'Enregistré (en attente de vérification)');
    } catch (err) {
      if (!(onAuthError && onAuthError(err))) setError(err.response?.data?.message || 'Erreur.');
    }
  };

  const toggleVerify = async (l) => {
    // Dé-publication rapide depuis la liste (repasser en attente).
    try {
      await updateRescue(l.id, { ...l, verified: !l.verified });
      await load();
    } catch (err) {
      onAuthError && onAuthError(err);
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`Supprimer « ${l.title} » ?`)) return;
    try {
      await deleteRescue(l.id);
      await load();
    } catch (err) {
      onAuthError && onAuthError(err);
    }
  };

  const pending = list.filter((l) => !l.verified);

  return (
    <div className="rescue-admin">
      <div className="rescue-admin__head">
        <span className="rescue-admin__count">
          {list.length} lien(s){pending.length > 0 && ` · ${pending.length} en attente`}
        </span>
        <button type="button" className="btn btn--amber admin__btn" onClick={openNew}>
          + Ajouter un lien
        </button>
      </div>

      {toast && <div className="admin__toast">{toast}</div>}

      {loading ? (
        <p className="admin__empty">…</p>
      ) : list.length === 0 ? (
        <p className="admin__empty">Aucun lien de préservation. Ajoute-en un.</p>
      ) : (
        <ul className="rescue-list">
          {list.map((l) => (
            <li key={l.id} className={`rescue-item ${l.verified ? '' : 'is-pending'}`}>
              <div className="rescue-item__main">
                <span className="rescue-item__title">{l.title}</span>
                <span className="rescue-item__meta">
                  <span className="rescue-item__type">{TYPES.find((t) => t.value === l.type)?.label || l.type}</span>
                  {' · '}appId {l.steamAppId}
                  {l.source && ` · ${l.source}`}
                </span>
                <a className="rescue-item__url" href={l.url} target="_blank" rel="noreferrer">
                  {l.url}
                </a>
              </div>
              <div className="rescue-item__actions">
                <span className={`rescue-item__badge ${l.verified ? 'is-ok' : 'is-wait'}`}>
                  {l.verified ? 'Publié' : 'En attente'}
                </span>
                <button className="admin__link" onClick={() => openEdit(l)}>Éditer</button>
                <button className="admin__link" onClick={() => toggleVerify(l)}>
                  {l.verified ? 'Dépublier' : 'Publier…'}
                </button>
                <button className="admin__link admin__link--danger" onClick={() => remove(l)}>Suppr.</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {form && (
        <div className="risk-form__overlay" onClick={() => setForm(null)}>
          <form className="risk-form rescue-form" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
            <h2>{form.id ? 'Éditer le lien' : 'Nouveau lien de préservation'}</h2>

            <div className="risk-form__grid">
              <label>
                SteamAppID
                <input type="number" value={form.steamAppId} onChange={setField('steamAppId')} required />
              </label>
              <label>
                Type
                <select value={form.type} onChange={setField('type')}>
                  {TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </label>
              <label className="risk-form__wide">
                Titre
                <input type="text" value={form.title} onChange={setField('title')} placeholder="Ex: The Crew Unlimited (serveur communautaire)" required />
              </label>
              <label className="risk-form__wide">
                Lien (site/GitHub/Discord officiel)
                <input type="url" value={form.url} onChange={setField('url')} placeholder="https://..." required />
              </label>
              <label className="risk-form__wide">
                Description (courte)
                <textarea rows="2" value={form.description} onChange={setField('description')} />
              </label>
              <label>
                Source (où trouvé)
                <input type="text" value={form.source} onChange={setField('source')} placeholder="PCGamingWiki, reddit…" />
              </label>
            </div>

            <div className="rescue-check">
              <div className="rescue-check__title">Check-list de sécurité (à cocher avant de publier)</div>
              {CHECKLIST.map((c) => (
                <label key={c.key} className={`rescue-check__item ${c.critical ? 'is-critical' : ''}`}>
                  <input
                    type="checkbox"
                    checked={!!checks[c.key]}
                    onChange={(e) => setChecks((s) => ({ ...s, [c.key]: e.target.checked }))}
                  />
                  <span>{c.label}{c.critical && <em className="rescue-check__req"> *</em>}</span>
                </label>
              ))}
            </div>

            <label className="rescue-form__publish">
              <input
                type="checkbox"
                checked={form.verified && criticalOk}
                disabled={!criticalOk}
                onChange={(e) => setForm((f) => ({ ...f, verified: e.target.checked }))}
              />
              <span>
                <strong>Publier ce lien</strong> (visible par les visiteurs)
                {!criticalOk && <em className="rescue-form__gate"> — coche d'abord les critères critiques (*)</em>}
              </span>
            </label>

            {error && <p className="risk-form__error">{error}</p>}

            <div className="risk-form__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setForm(null)}>Annuler</button>
              <button type="submit" className="btn btn--amber">Enregistrer</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default AdminRescue;
